import * as fs from "fs";
import { ForgeState } from "@/apps/forge/states/ForgeState";
import { FileLocationType } from "@/apps/forge/enum/FileLocationType";
import { EditorFileOptions } from "@/apps/forge/interfaces/EditorFileOptions";
import { pathParse } from "@/apps/forge/helpers/PathParse";
import { EventListenerModel } from "@/apps/forge/EventListenerModel";
import * as KotOR from "@/KotOR";
import { ProjectFileSystem } from "@/apps/forge/ProjectFileSystem";
import { EditorFileProtocol } from "@/apps/forge/enum/EditorFileProtocol";

export type EditorFileEventListenerTypes =
  'onNameChanged'|'onSaveStateChanged'|'onSaved'

export interface EditorFileEventListeners {
  onNameChanged: Function[],
  onSaveStateChanged: Function[],
  onSaved: Function[],
}

export interface EditorFileReadResponse {
  buffer: Uint8Array;
  buffer2?: Uint8Array;
}

/**
 * Forge resolves resources via self-describing **reference URIs** (not raw OS paths everywhere).
 *
 * **Output:** Call {@link EditorFile.toReferenceURI} for stable equality and `.forge/settings.json` `open_files`.
 * **Input:** Prefer {@link EditorFile.fromReference} / {@link EditorFile.revive}.
 *
 * **`getPath`** returns a shorthand (bare relative fragment or legacy `archive?res.ext`). Use `toReferenceURI()` for persistence and dedupe.
 */
export class EditorFile extends EventListenerModel {

  protocol: EditorFileProtocol;

  //handle - is for file handling inside the web environment
  handle?: FileSystemFileHandle;
  handle2?: FileSystemFileHandle; //for dual file types like mdl/mdx
  useGameFileSystem: boolean = false;
  useProjectFileSystem: boolean = false;
  useSystemFileSystem: boolean = false;
  trackRecent: boolean = true;

  buffer: Uint8Array = new Uint8Array(0);
  buffer2?: Uint8Array; //for dual file types like mdl/mdx
  /**
   * Set when the path ends with `.mdl.ascii`: Aurora MDL as text only (no `.mdx`).
   * Skips paired MDX loading in {@link readFile}.
   */
  mdlAsciiOnly: boolean = false;
  gffObject?: KotOR.GFFObject;
  isBlueprint: boolean = false;
  
  path: any;
  path2: any; //for dual file types like mdl/mdx
  archive_path: any;
  archive_path2: any; //for dual file types like mdl/mdx

  location: any;
  _unsaved_changes: any;
  _resref: any;
  _reskey: any;
  _ext: any;

  get unsaved_changes(){
    return this._unsaved_changes;
  };

  set unsaved_changes(value){
    this._unsaved_changes = !!value;
    this.processEventListener<EditorFileEventListenerTypes>('onSaveStateChanged', [this]);
    if(!this.unsaved_changes && this.trackRecent) ForgeState.addRecentFile(this);
  }

  get resref(){
    return this._resref;
  }

  set resref(value){
    this._resref = value;
    this.processEventListener<EditorFileEventListenerTypes>('onNameChanged', [this]);
  }

  get reskey(){
    return this._reskey;
  }

  set reskey(value){
    // console.log('reskey', value);
    this._reskey = value;
    this._ext = KotOR.ResourceTypes.getKeyByValue(this.reskey);
    this.processEventListener<EditorFileEventListenerTypes>('onNameChanged', [this]);
  }

  get ext(){
    return this._ext;
  }

  set ext(value){
    // console.log('ext', value);
    this._ext = value;
    this._reskey = KotOR.ResourceTypes[value];
    this.processEventListener<EditorFileEventListenerTypes>('onNameChanged', [this]);
  }

  constructor( options: EditorFileOptions = {} ){
    super();
    options = Object.assign({
      path: null,
      path2: null,
      handle: this.handle,
      handle2: this.handle2,
      buffer: [],
      buffer2: [],
      resref: null,
      reskey: null,
      ext: null,
      archive_path: null,
      location: FileLocationType.OTHER,
      useGameFileSystem: false,
      useProjectFileSystem: false,
      useSystemFileSystem: false,
    }, options);

    // Captured before the reskey/ext setters cross-trample each other; used as
    // a last-resort fallback so non-engine extensions (tsv, bat, ps1, ...) are
    // not lost when ResourceTypes has no entry for them.
    const rawOptionsExt = typeof options.ext === 'string' && options.ext.length
      ? options.ext.toLowerCase()
      : null;

    this.buffer = options.buffer instanceof Uint8Array ? options.buffer : new Uint8Array(0);
    this.buffer2 = options.buffer2;
    this.mdlAsciiOnly = !!options.mdlAsciiOnly;
    this.path = options.path;
    this.path2 = options.path2;
    this.ext = options.ext;
    this.resref = options.resref;
    this.reskey = options.reskey;
    this.archive_path = options.archive_path;
    this.location = options.location;
    this.handle = options.handle;
    this.handle2 = options.handle2;
    this.useGameFileSystem = !!options.useGameFileSystem;
    this.useProjectFileSystem = !!options.useProjectFileSystem;
    this.useSystemFileSystem = !!options.useSystemFileSystem;
    this.trackRecent = options.trackRecent !== false;

    if(!this.ext && this.reskey){
      this.ext = KotOR.ResourceTypes.getKeyByValue(this.reskey);
    }
    if(!this.ext && rawOptionsExt){
      this.ext = rawOptionsExt;
    }

    this.setPath(this.path);

    if(!this.ext && this.reskey){
      this.ext = KotOR.ResourceTypes.getKeyByValue(this.reskey);
    }
    if(!this.ext && rawOptionsExt){
      this.ext = rawOptionsExt;
    }

    // Only mark in-memory/untitled files dirty by default.
    // Files loaded from disk/archive should start clean.
    const isUntitledInMemoryFile =
      this.location == FileLocationType.OTHER &&
      !this.path &&
      !this.archive_path &&
      !this.handle;
    this.unsaved_changes = isUntitledInMemoryFile;

  }

  setGFFObject(gffObject: KotOR.GFFObject){
    this.gffObject = gffObject;
  }

  setPath(filepath: string){
    this.path = filepath;
    if(typeof this.path === 'string'){
      this.path = filepath.replace(/\\/g, "/");
      const hasProtocol = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(this.path);
      const parsedURL = hasProtocol ? new URL(this.path) : null;
      const searchParams = parsedURL ? parsedURL.searchParams : new URLSearchParams();

      this.protocol = (parsedURL ? parsedURL.protocol : EditorFileProtocol.FILE) as EditorFileProtocol;
      let pathname = (parsedURL ? parsedURL.pathname : this.path).replace(/%20/g, " ");
      try {
        pathname = decodeURIComponent(pathname);
      } catch {
        // keep the partially decoded path
      }

      //remove excess slashes on both ends
      pathname = pathname.replace(/^\/+|\/+$/g, '');

      // file://game.dir/Saves/... puts the virtual root in URL.hostname, not pathname.
      const virtualHost = String(parsedURL?.hostname || '').toLowerCase();
      if(virtualHost === 'game.dir' || pathname.toLowerCase().indexOf('game.dir') >= 0){
        pathname = pathname.replace(/game\.dir/ig, '').replace(/^\/+|\/+$/g, '');
        this.useGameFileSystem = true;
      }

      if(virtualHost === 'project.dir' || pathname.toLowerCase().indexOf('project.dir') >= 0){
        pathname = pathname.replace(/project\.dir/ig, '').replace(/^\/+|\/+$/g, '');
        this.useProjectFileSystem = true;
      }

      if(virtualHost === 'system.dir' || pathname.toLowerCase().indexOf('system.dir') >= 0){
        pathname = pathname.replace(/system\.dir/ig, '').replace(/^\/+|\/+$/g, '');
        this.useSystemFileSystem = true;
      }
      pathname = pathname.replace(/^\/+|\/+$/g, '');

      const path_obj = pathParse(pathname);
      switch(this.protocol){
        case EditorFileProtocol.BIF:
        case EditorFileProtocol.ERF:
        case EditorFileProtocol.MOD:
        case EditorFileProtocol.RIM:
        case EditorFileProtocol.ZIP:
        case EditorFileProtocol._7ZIP:
          this.location = FileLocationType.ARCHIVE;
          this.archive_path = pathname;

          if(searchParams.has('resref')){
            this.resref = searchParams.get('resref');
          }

          if(searchParams.has('restype')){
            const ext = searchParams.get('restype') as string;
            this.ext = KotOR.ResourceTypes.getKeyByValue( ext );

            if(!this.reskey){
              this.reskey = KotOR.ResourceTypes[ext];
            }
          }

        break;
        case EditorFileProtocol.FILE:
          this.location = FileLocationType.LOCAL;
          this.path = pathname;
          this.resref = path_obj.name;
          if (path_obj.ext === "mdl.ascii") {
            this.mdlAsciiOnly = true;
            this.reskey = KotOR.ResourceTypes.mdl;
          } else if (!this.reskey) {
            this.reskey = KotOR.ResourceTypes[path_obj.ext];
          }

          // Prefer the canonical ext spelling derived from ResourceTypes; if the
          // parsed extension is not registered as an engine resource (e.g. tsv,
          // bat, ps1, json variants outside the table), fall back to the raw
          // extension so dispatchers and tab titles still see it.
          this.ext = KotOR.ResourceTypes.getKeyByValue(this.reskey)
            || (path_obj.ext ? path_obj.ext.toLowerCase() : this.ext);
        break;
        default:
          console.warn('Unhandled Protocol', this.protocol, filepath);
        break;
      }
      console.log('setPath', this);
    }
  }

  getPath(){
    //Check to see if the EditorFile has the path variable set.
    //If not it's because the file was created in memory and hasn't been saved to the HDD yet
    if(this.path && !this.archive_path){
      return this.path;
    }else if(this.archive_path){
      return this.archive_path + '?' + this.resref + '.' + this.ext;
    }
    return undefined;
  }

  /** Normalize a path segment inside `game.dir` / `project.dir` / archive-relative parts. */
  static normalizeReferenceSegment(seg: string): string {
    return String(seg ?? '').trim().replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
  }

  static referenceURIForProjectRelative(rel: string): string {
    const r = EditorFile.normalizeReferenceSegment(rel);
    return `${EditorFileProtocol.FILE}//project.dir/${r}`;
  }

  static referenceURIForGameRelative(rel: string): string {
    const r = EditorFile.normalizeReferenceSegment(rel);
    return `${EditorFileProtocol.FILE}//game.dir/${r}`;
  }

  static referenceURIForSystemVirtualName(fileName: string): string {
    const r = EditorFile.normalizeReferenceSegment(fileName);
    return `${EditorFileProtocol.FILE}//system.dir/${r}`;
  }

  static referenceURIForArchiveResource(
    scheme: 'bif' | 'rim' | 'erf',
    archiveRel: string,
    resref: string,
    restype: string,
  ): string {
    const arch = EditorFile.normalizeReferenceSegment(archiveRel);
    return `${scheme}://game.dir/${arch}?resref=${resref}&restype=${restype}`;
  }

  /** Map protocol enum/string to persisted archive scheme (`mod`/`.sav` archives use `erf`). */
  private static persistedArchiveSchemeFromProtocol(protocolLike: EditorFileProtocol | string | undefined): 'bif' | 'rim' | 'erf' {
    const raw = String(protocolLike ?? '').replace(/:/g, '').toLowerCase();
    if(raw === 'bif'){
      return 'bif';
    }
    if(raw === 'rim'){
      return 'rim';
    }
    return 'erf';
  }

  /** `file:///C:/foo` style for Electron absolute paths; bare Unix absolute → `file://`. */
  static diskPathToFileURI(diskPath: string): string | undefined {
    const n = String(diskPath ?? '').trim().replace(/\\/g, '/');
    if(!n.length){
      return undefined;
    }
    if(/^[a-zA-Z]:/.test(n)){
      return `file:///${n}`;
    }
    if(n.startsWith('/')){
      return `file://${n}`;
    }
    return undefined;
  }

  /**
   * Canonical self-describing reference for Forge lists and `.forge/settings.json`.
   * **Not** the same string as {@link getPath}.
   */
  toReferenceURI(): string | undefined {
    if(this.archive_path && this.resref != null && this.ext != null){
      const scheme = EditorFile.persistedArchiveSchemeFromProtocol(this.protocol);
      const arch = EditorFile.normalizeReferenceSegment(String(this.archive_path));
      return `${scheme}://game.dir/${arch}?resref=${String(this.resref)}&restype=${String(this.ext)}`;
    }

    if(typeof this.path !== 'string' || !this.path.length){
      return undefined;
    }

    if(this.protocol === EditorFileProtocol.FILE || !this.archive_path){
      const rel = EditorFile.normalizeReferenceSegment(this.path);
      if(this.useProjectFileSystem){
        return `${EditorFileProtocol.FILE}//project.dir/${rel}`;
      }
      if(this.useGameFileSystem){
        return `${EditorFileProtocol.FILE}//game.dir/${rel}`;
      }
      if(this.useSystemFileSystem){
        return `${EditorFileProtocol.FILE}//system.dir/${rel}`;
      }
      const disk = EditorFile.diskPathToFileURI(this.path);
      if(disk){
        return disk;
      }
    }

    return undefined;
  }

  static fromReference(uri: string): EditorFile {
    return new EditorFile({ path: uri.trim() });
  }

  /**
   * Rebuild state from persisted JSON/tab blobs: prefer composing a reference URI then `setPath`.
   */
  static referenceStringFromPlain(plain: Partial<EditorFile> & EditorFileOptions): string | undefined {
    const p = plain.path;
    if(typeof p === 'string' && /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(p.trim())){
      return p.trim();
    }

    const ap = plain.archive_path;
    if(ap != null && plain.resref != null && plain.ext != null){
      const scheme = EditorFile.persistedArchiveSchemeFromProtocol((plain as EditorFile).protocol ?? EditorFileProtocol.ERF);
      return EditorFile.referenceURIForArchiveResource(
        scheme,
        String(ap),
        String(plain.resref),
        String(plain.ext),
      );
    }

    if(typeof p === 'string' && p.length){
      const rel = EditorFile.normalizeReferenceSegment(p);
      if(plain.useProjectFileSystem){
        return EditorFile.referenceURIForProjectRelative(rel);
      }
      if(plain.useGameFileSystem){
        return EditorFile.referenceURIForGameRelative(rel);
      }
      if(plain.useSystemFileSystem){
        return EditorFile.referenceURIForSystemVirtualName(rel);
      }
      const disk = EditorFile.diskPathToFileURI(p);
      if(disk){
        return disk;
      }
    }

    return undefined;
  }

  private static overlayHydratedTransientState(target: EditorFile, src: Partial<EditorFile>): void {
    if(src.buffer instanceof Uint8Array){
      target.buffer = src.buffer;
    }
    if(src.buffer2 instanceof Uint8Array){
      target.buffer2 = src.buffer2;
    }
    if(src.handle){
      target.handle = src.handle;
    }
    if(src.handle2){
      target.handle2 = src.handle2;
    }
    if(src.gffObject){
      target.gffObject = src.gffObject;
    }
    if(typeof src.isBlueprint === 'boolean'){
      target.isBlueprint = src.isBlueprint;
    }
    if(typeof src.path2 === 'string' && src.path2.length){
      target.path2 = src.path2.replace(/\\/g, '/');
    }
    if(src.mdlAsciiOnly){
      target.mdlAsciiOnly = true;
    }
  }

  static revive(plain: Partial<EditorFile> | EditorFileOptions | Record<string, unknown>): EditorFile {
    const p = plain as Partial<EditorFile> & EditorFileOptions;
    const ref = EditorFile.referenceStringFromPlain(p);
    if(ref){
      const f = EditorFile.fromReference(ref);
      EditorFile.overlayHydratedTransientState(f, p);
      return f;
    }
    return EditorFile.From(p as EditorFile);
  }

  /** Allowed reference schemes for persisted `open_files` (strict). */
  static isValidForgePersistedReference(uri: string): boolean {
    const t = typeof uri === 'string' ? uri.trim() : '';
    if(!t.length || !/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(t)){
      return false;
    }
    const scheme = t.split(':')[0].toLowerCase();
    return scheme === 'file' || scheme === 'bif' || scheme === 'rim' || scheme === 'erf' || scheme === 'mod';
  }

  async readFile(): Promise<EditorFileReadResponse> {
    return new Promise<EditorFileReadResponse>( async (resolve, reject) => {
      try {
      if (this.reskey == KotOR.ResourceTypes.mdl || this.reskey == KotOR.ResourceTypes.mdx) {
        if (this.mdlAsciiOnly) {
          resolve(await this.readMdlAsciiOnlyFile());
        } else {
          resolve(await this.readMdlMdxFile());
        }
      } else {
        //Common Loader
        if(this.buffer instanceof Uint8Array && this.buffer.length){
          resolve({
            buffer: this.buffer,
          });
        }else{
          if(this.archive_path){
            const archive_path = pathParse(this.archive_path);
            console.log(archive_path.ext)

            switch(this.protocol){
              case EditorFileProtocol.BIF: {
                let bif = KotOR.BIFManager.FindByPath(this.archive_path);
                if(!bif){
                  bif = new KotOR.BIFObject(this.archive_path);
                  await bif.load();
                }
                this.buffer = await bif.getResourceBuffer(bif.getResource(this.resref, this.reskey));
                resolve({
                  buffer: this.buffer,
                });
              }
              break;
              case EditorFileProtocol.ERF:
              case EditorFileProtocol.MOD: {
                let erf = KotOR.ERFManager.FindByPath(this.archive_path);
                if(!erf){
                  erf = new KotOR.ERFObject(this.archive_path);
                  await erf.load();
                }
                this.buffer = await erf.getResourceBufferByResRef(this.resref, this.reskey);
                resolve({
                  buffer: this.buffer,
                });
              }
              break;
              case EditorFileProtocol.RIM: {
                let rim = KotOR.RIMManager.FindByPath(this.archive_path);
                if(!rim){
                  rim = new KotOR.RIMObject(this.archive_path);
                  await rim.load();
                }
                this.buffer = await rim.getResourceBuffer(rim.getResourceInfo(this.resref, this.reskey));
                resolve({
                  buffer: this.buffer,
                });
              }
              break;
              default:
                console.warn('EditorFile.readFile', 'unhandled protocol', this.protocol);
              break;
            }
          }else{
            if(typeof this.path === 'string'){
              switch(this.protocol){
                case EditorFileProtocol.FILE:
                  if(this.useGameFileSystem){
                      KotOR.GameFileSystem.readFile(this.path).then( (buffer: Uint8Array) => {
                      this.buffer = buffer;
        
                      resolve({
                        buffer: this.buffer,
                      });
                    }).catch( (err: any) => {
                      reject(err);
                    });
                  }else if(this.useProjectFileSystem){
                    ProjectFileSystem.readFile(this.path).then( (buffer: Uint8Array) => {
                      this.buffer = buffer;
        
                      resolve({
                        buffer: this.buffer,
                      });
                    }).catch( (err: any) => {
                      reject(err);
                    });
                  }else{
                    if(KotOR.ApplicationProfile.ENV == KotOR.ApplicationEnvironment.ELECTRON){
                      fs.readFile(this.path, (err, buffer) => {
                        if(err){
                          reject(err);
                          return;
                        }
      
                        this.buffer = new Uint8Array(buffer);
                        resolve({
                          buffer: this.buffer,
                        });
                      });
                    }else{
                      if(this.handle){
                        let granted = (await this.handle.queryPermission({mode: 'read'})) === 'granted';
                        if(!granted){
                          granted = (await this.handle.requestPermission({mode: 'read'})) === 'granted';
                        }
                        
                        if(granted){
                          let file = await this.handle.getFile();
                          this.buffer = new Uint8Array( await file.arrayBuffer() );
                          resolve({
                            buffer: this.buffer,
                          });
                        }else{
                          //cannot open file
                          console.warn('EditorFile.readFile', 'unable to open file', this.protocol);
                          this.buffer = new Uint8Array(0);
                          resolve({
                            buffer: this.buffer,
                          });
                        }
                      }
                    }
                  }
                break;
                default:
                  console.warn('EditorFile.readFile', 'unhandled protocol', this.protocol);
                break;
              }
            }else{
              console.warn('EditorFile.readFile', 'unable to open file', this.protocol);
              this.buffer = new Uint8Array(0);
              resolve({
                buffer: this.buffer,
              });
            }
          }
  
        }
      }
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Load a single on-disk file (`*.mdl.ascii`) with no MDX; used by the model viewer for ASCII exports.
   */
  async readMdlAsciiOnlyFile(): Promise<EditorFileReadResponse> {
    return new Promise<EditorFileReadResponse>(async (resolve, reject) => {
      if (this.buffer instanceof Uint8Array && this.buffer.length) {
        resolve({ buffer: this.buffer });
        return;
      }
      if (!this.path) {
        resolve({ buffer: new Uint8Array(0) });
        return;
      }
      if (this.protocol !== EditorFileProtocol.FILE) {
        resolve({ buffer: new Uint8Array(0) });
        return;
      }
      try {
        if (this.useGameFileSystem) {
          this.buffer = await KotOR.GameFileSystem.readFile(this.path);
        } else if (this.useProjectFileSystem) {
          this.buffer = await ProjectFileSystem.readFile(this.path);
        } else if (KotOR.ApplicationProfile.ENV === KotOR.ApplicationEnvironment.ELECTRON) {
          await new Promise<void>((res, rej) => {
            fs.readFile(this.path, (err, buf) => {
              if (err) {
                rej(err);
                return;
              }
              this.buffer = new Uint8Array(buf);
              res();
            });
          });
        } else if (this.handle) {
          let granted = (await this.handle.queryPermission({ mode: "read" })) === "granted";
          if (!granted) {
            granted = (await this.handle.requestPermission({ mode: "read" })) === "granted";
          }
          if (!granted) {
            resolve({ buffer: new Uint8Array(0) });
            return;
          }
          const file = await this.handle.getFile();
          this.buffer = new Uint8Array(await file.arrayBuffer());
        }
      } catch (e) {
        console.error("EditorFile.readMdlAsciiOnlyFile", e);
      }
      resolve({ buffer: this.buffer ?? new Uint8Array(0) });
    });
  }

  async readMdlMdxFile(): Promise<EditorFileReadResponse> {
    return new Promise<EditorFileReadResponse>( async (resolve, reject) => {
      if(this.archive_path){
        switch(this.protocol){
          case EditorFileProtocol.BIF:
            try{
              const key_mdl = KotOR.KEYManager.Key.getFileKey(this.resref, KotOR.ResourceTypes['mdl']);
              const key_mdx = KotOR.KEYManager.Key.getFileKey(this.resref, KotOR.ResourceTypes['mdx']);

              if((!(this.buffer instanceof Uint8Array) || !this.buffer?.length) && key_mdl){
                this.buffer = await KotOR.KEYManager.Key.getFileBuffer(key_mdl);
              }

              if((!(this.buffer2 instanceof Uint8Array) || !this.buffer2?.length) && key_mdx){
                this.buffer2 = await KotOR.KEYManager.Key.getFileBuffer(key_mdx);
              }
            }catch(e){
              console.warn('EditorFile.readMdlMdxFile BIF read failed', e);
            }
            
            resolve({
              buffer: this.buffer instanceof Uint8Array ? this.buffer : new Uint8Array(0),
              buffer2: this.buffer2 instanceof Uint8Array ? this.buffer2 : new Uint8Array(0),
            });
          break;
          case EditorFileProtocol.ERF:
          case EditorFileProtocol.MOD: {
            let erf = KotOR.ERFManager.FindByPath(this.archive_path);
            if(!erf){
              erf = new KotOR.ERFObject(this.archive_path);
              await erf.load();
            }
            if(!(this.buffer instanceof Uint8Array) || !this.buffer?.length){
              this.buffer = await erf.getResourceBufferByResRef(this.resref, KotOR.ResourceTypes['mdl']);
            }
            if(!(this.buffer2 instanceof Uint8Array) || !this.buffer2?.length){
              this.buffer2 = await erf.getResourceBufferByResRef(this.resref, KotOR.ResourceTypes['mdx']);
            }
            resolve({
              buffer: this.buffer,
              buffer2: this.buffer2
            });
          }
          break;
          case EditorFileProtocol.RIM: {
            let rim = KotOR.RIMManager.FindByPath(this.archive_path);
            if(!rim){
              rim = new KotOR.RIMObject(this.archive_path);
              await rim.load();
            }
            if(!(this.buffer instanceof Uint8Array) || !this.buffer?.length){
              this.buffer = await rim.getResourceBufferByResRef(this.resref, KotOR.ResourceTypes['mdl']);
            }
            if(!(this.buffer2 instanceof Uint8Array) || !this.buffer2?.length){
              this.buffer2 = await rim.getResourceBufferByResRef(this.resref, KotOR.ResourceTypes['mdx']);
            }
            resolve({
              buffer: this.buffer,
              buffer2: this.buffer2
            });
          }
          break;
          default:

          break;
        }
      }else{
        switch(this.protocol){
          case EditorFileProtocol.FILE:
            if(this.useGameFileSystem){
              try{
                //MDL
                if(!(this.buffer instanceof Uint8Array) || !this.buffer?.length) this.buffer = await KotOR.GameFileSystem.readFile(this.path);

                //MDX
                if(!(this.buffer2 instanceof Uint8Array) || !this.buffer2?.length) this.buffer2 = await KotOR.GameFileSystem.readFile(this.path2);
              }catch(e){
                console.error(e);
              }
  
              resolve({
                buffer: this.buffer,
                buffer2: this.buffer2
              });
            }else if(this.useProjectFileSystem){
              try{
                //MDL
                if(!(this.buffer instanceof Uint8Array) || !this.buffer?.length) this.buffer = await ProjectFileSystem.readFile(this.path);
                
                //MDX
                if(!(this.buffer2 instanceof Uint8Array) || !this.buffer2?.length) this.buffer2 = await ProjectFileSystem.readFile(this.path2);
              }catch(e){
                console.error(e);
              }
  
              resolve({
                buffer: this.buffer,
                buffer2: this.buffer2,
              });
            }else{
              if(KotOR.ApplicationProfile.ENV == KotOR.ApplicationEnvironment.ELECTRON){
                fs.readFile(this.path, (err, buffer) => {
                  if(err) throw err;

                  this.buffer = new Uint8Array(buffer);
                  fs.readFile(this.path2, (err, buffer2) => {
                    if(err) throw err;

                    this.buffer2 = new Uint8Array(buffer2);
                    resolve({
                      buffer: this.buffer,
                      buffer2: this.buffer2,
                    });
                  });
                });
              }else{
                //MDL
                let granted = false;
                if(this.handle){
                  granted = (await this.handle.queryPermission({mode: 'readwrite'})) === 'granted';
                  if(!granted){
                    granted = (await this.handle.requestPermission({mode: 'readwrite'})) === 'granted';
                  }

                  if(!granted){
                    console.warn('EditorFile.readFile', 'unable to open (mdl) file', this.protocol);
                    resolve({
                      buffer: this.buffer,
                      buffer2: this.buffer2,
                    });
                    return;
                  }

                  let file = await this.handle.getFile();
                  if(file){
                    this.buffer = new Uint8Array( await file.arrayBuffer() );
                  }
                }

                //MDX
                let granted2 = false;
                if(this.handle2){
                  granted2 = (await this.handle2.queryPermission({mode: 'read'})) === 'granted';
                  if(!granted2){
                    granted2 = (await this.handle2.requestPermission({mode: 'read'})) === 'granted';
                  }

                  if(!granted2){
                    console.warn('EditorFile.readFile', 'unable to open (mdx) file', this.protocol);
                    resolve({
                      buffer: this.buffer,
                      buffer2: this.buffer2,
                    });
                    return;
                  }
                  
                  let file2 = await this.handle2.getFile();
                  if(file2){
                    this.buffer2 = new Uint8Array( await file2.arrayBuffer() );
                  }
                }

                resolve({
                  buffer: this.buffer,
                  buffer2: this.buffer2,
                });
              }
            }
          break;
          default:
            console.warn('EditorFile.readFile', 'unhandled protocol', this.protocol);
          break;
        }
      }
    });
  }

  getData(){
    return this.buffer;
  }

  getLocalPath(){
    if(!this.archive_path && this.path)
      return this.path;
    else
      return null;
  }

  getFilename(){
    if (this.mdlAsciiOnly) {
      return `${this.resref}.mdl.ascii`;
    }
    return this.resref + "." + this.ext;
  }

  getPrettyPath(){
    if(!this.path && this.archive_path){
      if(this.useGameFileSystem || this.useProjectFileSystem){
        return `${this.protocol}//~/${this.archive_path}`;
      }
      return `${this.archive_path}`;
    }
    if(!this.path){
      return '';
    }

    const parsed = pathParse(this.path);
    if(this.useGameFileSystem){
      if(this.archive_path){
        return `${this.protocol}//~/${this.archive_path}`;
      }
      
      return parsed.dir;
    }else if (this.useProjectFileSystem){
      if(this.archive_path){
        return `${this.protocol}//~/${this.archive_path}`;
      }
      
      return parsed.dir;
    }

    if(this.archive_path){
      return `${this.archive_path}`;
    }
      
    return parsed.dir;
  }

  /**
   * Write bytes using the same backend matrix as {@link readFile}
   * (`game.dir` / `project.dir` / Electron disk / browser file handle).
   * Archives and untitled files return false so the caller can Save As.
   */
  async writeBuffer(data: Uint8Array): Promise<boolean> {
    if(!(data instanceof Uint8Array)){
      return false;
    }
    if(this.archive_path || this.archive_path2){
      return false;
    }
    try{
      if(this.useProjectFileSystem){
        if(!this.path || !ProjectFileSystem.hasRoot()){
          return false;
        }
        const ok = await ProjectFileSystem.writeFile(this.path, data);
        if(ok){
          this.buffer = data;
          this.unsaved_changes = false;
        }
        return ok;
      }
      if(this.useGameFileSystem){
        if(!this.path){
          return false;
        }
        const ok = await KotOR.GameFileSystem.writeFile(this.path, data);
        if(ok){
          this.buffer = data;
          this.unsaved_changes = false;
        }
        return ok;
      }
      if(KotOR.ApplicationProfile.ENV == KotOR.ApplicationEnvironment.ELECTRON){
        if(!this.path){
          return false;
        }
        await fs.promises.writeFile(this.path, data);
        this.buffer = data;
        this.unsaved_changes = false;
        return true;
      }
      if(this.handle){
        let granted = (await this.handle.queryPermission({mode: 'readwrite'})) === 'granted';
        if(!granted){
          granted = (await this.handle.requestPermission({mode: 'readwrite'})) === 'granted';
        }
        if(!granted){
          return false;
        }
        const stream = await this.handle.createWritable();
        await stream.write(data as any);
        await stream.close();
        this.buffer = data;
        this.unsaved_changes = false;
        return true;
      }
      return false;
    }catch(e){
      console.error('EditorFile.writeBuffer', this.path, e);
      return false;
    }
  }

  async save(): Promise<boolean> {
    return this.writeBuffer(this.buffer);
  }

  saveAs(){
    //stub
  }

  static From(editorFile: EditorFile){
    return new EditorFile(editorFile as EditorFileOptions);
  }

}
